import { defineStore } from 'pinia'
import { ref, reactive, computed, watch, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useEventStore } from '@/stores/eventStore.js'

export const useStatisticsStore = defineStore('useStatisticsStore', () => {
  const { t } = useI18n()
  const eventStore = useEventStore()

  const lightColors = [
    '#e25555',
    '#4faf75',
    '#4090ad',
    '#f6d845',
    '#fb9e40',
    '#ab56ab',
    '#4591b2',
    '#a7dd71',
    '#59abfb',
    '#d16fa0',
    '#ab5cfd',
    '#5bd1d1',
    '#db6b6b',
    '#d6d653',
    '#4f77c1',
    '#a09696'
  ]
  const darkColors = [
    '#e25555',
    '#4faf75',
    '#4090ad',
    '#f6d845',
    '#fb9e40',
    '#ab56ab',
    '#4591b2',
    '#a7dd71',
    '#59abfb',
    '#d16fa0',
    '#ab5cfd',
    '#5bd1d1',
    '#db6b6b',
    '#d6d653',
    '#4f77c1',
    '#a09696'
  ]
  const statusColors = ['#4faf75', 'grey', '#59abfb', '#e07772', 'silver']

  const queryBody = computed(() => ({
    event_id: eventStore.event?.id || 0,
    sort: [{ name: 'id', order: 'ASC' }]
  }))

  // Estado de configuración
  const showConfig = ref(false)
  const sortMode = ref(false)

  // Key para localStorage específico del evento
  const STORAGE_KEY = computed(() => `statistics_config_${eventStore.event?.id || 'default'}`)

  // Configuración por defecto de statistics
  const defaultStatistics = [
    {
      title: t('Event info'),
      active: true,
      component: 'EventInfo',
      hasCharts: false
    },
    {
      title: t('Personas'),
      active: true,
      component: 'Personas',
      hasCharts: true,
      charts: [
        {
          key: 'statusPersona',
          title: t('Status'),
          active: true,
          displayMode: 'both' as 'chart' | 'totals' | 'both',
          legend: false,
          chartType: 'doughnut'
        },
        {
          key: 'groupsPersona',
          title: t('Professional group'),
          active: true,
          displayMode: 'both' as 'chart' | 'totals' | 'both',
          legend: false,
          chartType: 'doughnut'
        },
        {
          key: 'rolesPersona',
          title: t('Role'),
          active: true,
          displayMode: 'both' as 'chart' | 'totals' | 'both',
          legend: false,
          chartType: 'doughnut'
        }
      ]
    },
    {
      title: t('Tickets sold'),
      active: true,
      component: 'Tickets',
      hasCharts: true,
      charts: [
        {
          key: 'completedTicket',
          title: t('Tickets'),
          active: true,
          displayMode: null,
          legend: false,
          chartType: 'doughnut'
        },
        {
          key: 'ticketCategory',
          title: `${t('By')} ${t('category')}`,
          active: true,
          displayMode: 'both' as 'chart' | 'totals' | 'both',
          legend: false,
          chartType: 'doughnut'
        },
        {
          key: 'ticketSubCategory',
          title: `${t('By')} ${t('subcategory')}`,
          active: true,
          displayMode: 'both' as 'chart' | 'totals' | 'both',
          legend: true,
          chartType: 'doughnut'
        },
        {
          key: 'ticketActivity',
          title: `${t('By')} ${t('activity')}`,
          active: true,
          displayMode: 'both' as 'chart' | 'totals' | 'both',
          legend: false,
          chartType: 'doughnut'
        }
      ]
    },
    {
      title: t('Payments'),
      active: true,
      component: 'Payments',
      hasCharts: false
    }
  ]

  // Estado reactivo de statistics
  const statistics = reactive([...defaultStatistics])

  // Cargar configuración desde localStorage
  const loadConfiguration = () => {
    if (!eventStore.event?.id) return

    try {
      const saved = localStorage.getItem(STORAGE_KEY.value)
      if (saved) {
        const config = JSON.parse(saved)

        // Restaurar orden de statistics si existe
        if (config.statistics && Array.isArray(config.statistics)) {
          statistics.splice(0, statistics.length, ...config.statistics)
        }
      }
    } catch (error) {
      console.error('Error loading statistics configuration:', error)
    }
  }

  // Guardar configuración en localStorage
  const saveConfiguration = () => {
    if (!eventStore.event?.id) return

    try {
      const config = {
        statistics: [...statistics]
      }
      localStorage.setItem(STORAGE_KEY.value, JSON.stringify(config))
      console.log('Configuración guardada:', config)
    } catch (error) {
      console.error('Error saving statistics configuration:', error)
    }
  }

  // Reordenar statistics (componentes padre)
  const reorderStatistics = (oldIndex: number, newIndex: number) => {
    const movedItem = statistics.splice(oldIndex, 1)[0]
    statistics.splice(newIndex, 0, movedItem)
    saveConfiguration()
  }

  // Reordenar charts dentro de un statistic
  const reorderCharts = (statisticComponent: string, newChartsOrder: any[]) => {
    const stat = statistics.find(s => s.component === statisticComponent)
    if (stat?.hasCharts && stat.charts) {
      stat.charts.splice(0, stat.charts.length, ...newChartsOrder)
      saveConfiguration()
    }
  }

  // Obtener configuración de charts para un componente
  const getChartsConfig = (stat: any) => {
    if (!stat.hasCharts || !stat.charts) return {}

    const config: { [key: string]: { active: boolean; displayMode: string; legend: boolean; chartType: string } } = {}
    stat.charts.forEach((chart: any) => {
      config[chart.key] = {
        active: chart.active,
        displayMode: chart.displayMode,
        legend: chart.legend,
        chartType: chart.chartType
      }
    })
    return config
  }

  // Watcher para cambios automáticos
  watch(
    () => statistics,
    () => {
      saveConfiguration()
    },
    { deep: true }
  )

  return {
    // Colores
    lightColors,
    darkColors,
    statusColors,

    // Query
    queryBody,

    // Configuración UI
    showConfig,
    sortMode,

    // Statistics
    statistics,
    defaultStatistics,

    // Funciones
    loadConfiguration,
    saveConfiguration,
    reorderStatistics,
    reorderCharts,
    getChartsConfig
  }
})
